/*
 * DataGrid — the report grid (NEXUS-1384 dashboard v2).
 *
 * Every column always shows a sort control (⇅ / ▲ / ▼) and a filter funnel. The funnel opens a
 * type-ahead dropdown: typing narrows the column's distinct values, clicking one (or Enter on the
 * highlighted one) toggles it as a filter. Several values in one column = OR; filters across
 * columns = AND. Pump / user columns render as links to that pump's page / the user's card.
 *
 * View state (sort + filters) is owned by the caller so the chart and the HTML export can use the
 * same filtered + sorted rows the grid shows.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReportColumn } from '../lib/reports';

export type Row = Record<string, string | number | null>;

export interface GridView {
  sortKey: string | null;
  sortDir: 'asc' | 'desc';
  /** column key → selected display values */
  filters: Record<string, string[]>;
}

export const EMPTY_VIEW: GridView = { sortKey: null, sortDir: 'asc', filters: {} };

export function formatCell(col: Pick<ReportColumn, 'type'>, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (col.type === 'datetime') {
    const d = new Date(String(v));
    return Number.isNaN(d.getTime())
      ? String(v)
      : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }
  if (col.type === 'date') {
    const d = new Date(`${String(v)}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? String(v)
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (col.type === 'number') return Number(v).toLocaleString();
  return String(v);
}

function sortValue(col: ReportColumn, v: unknown): number | string {
  if (v === null || v === undefined || v === '') return col.type === 'text' ? '' : -Infinity;
  if (col.type === 'number') return Number(v);
  if (col.type === 'datetime' || col.type === 'date') {
    const t = Date.parse(col.type === 'date' ? `${String(v)}T00:00:00` : String(v));
    return Number.isNaN(t) ? -Infinity : t;
  }
  return String(v).toLowerCase();
}

/** Filtered + sorted rows for a view. */
export function applyView(columns: ReportColumn[], rows: Row[], view: GridView): Row[] {
  let out = rows;
  for (const c of columns) {
    const sel = view.filters[c.key];
    if (!sel || sel.length === 0) continue;
    const set = new Set(sel);
    out = out.filter((r) => set.has(formatCell(c, r[c.key])));
  }
  const col = view.sortKey ? columns.find((c) => c.key === view.sortKey) : undefined;
  if (col) {
    const m = view.sortDir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
      const av = sortValue(col, a[col.key]);
      const bv = sortValue(col, b[col.key]);
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * m;
      return (av === bv ? 0 : (av as number) < (bv as number) ? -1 : 1) * m;
    });
  }
  return out;
}

export function activeFilterCount(view: GridView): number {
  return Object.values(view.filters).reduce((n, v) => n + (v?.length ?? 0), 0);
}

export function linkFor(col: ReportColumn, row: Row): string | null {
  if (!col.link) return null;
  const id = row[col.link.idKey];
  if (id === null || id === undefined || id === '') return null;
  if (col.link.kind === 'pump') return `/pumps/${encodeURIComponent(String(id))}`;
  if (col.link.kind === 'issue') return `/issues/${encodeURIComponent(String(id))}`;
  return `/users?user=${encodeURIComponent(String(id))}`;
}

// ── Header cell ─────────────────────────────────────────────────────────

function SortGlyph({ state }: { state: 'none' | 'asc' | 'desc' }) {
  return (
    <svg width="9" height="12" viewBox="0 0 9 12" aria-hidden style={{ verticalAlign: 'middle' }}>
      <path d="M4.5 0 9 5H0z" fill="currentColor" opacity={state === 'asc' ? 1 : 0.3} />
      <path d="M4.5 12 0 7h9z" fill="currentColor" opacity={state === 'desc' ? 1 : 0.3} />
    </svg>
  );
}

function FilterGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden style={{ verticalAlign: 'middle' }}>
      <path d="M1.5 2h13l-5 6v5.5l-3 1.5V8z" />
    </svg>
  );
}

interface HeadProps {
  col: ReportColumn;
  values: string[];
  view: GridView;
  onView: (v: GridView) => void;
}

function HeadCell({ col, values, view, onView }: HeadProps) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const selected = view.filters[col.key] ?? [];
  const sortState = view.sortKey === col.key ? view.sortDir : 'none';
  const numeric = col.type === 'number';

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? values.filter((v) => v.toLowerCase().includes(t)) : values;
  }, [values, q]);

  useEffect(() => setHi(0), [q]);

  const cycleSort = () => {
    if (view.sortKey !== col.key) {
      onView({ ...view, sortKey: col.key, sortDir: numeric ? 'desc' : 'asc' });
    } else if (view.sortDir === (numeric ? 'desc' : 'asc')) {
      onView({ ...view, sortDir: numeric ? 'asc' : 'desc' });
    } else {
      onView({ ...view, sortKey: null });
    }
  };

  const setSel = (next: string[]) =>
    onView({ ...view, filters: { ...view.filters, [col.key]: next } });
  const toggle = (v: string) =>
    setSel(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (open) return setOpen(false);
    const r = e.currentTarget.getBoundingClientRect();
    const left = Math.min(r.left, window.innerWidth - 270);
    setAnchor({ top: r.bottom + 4, left: Math.max(8, left) });
    setQ('');
    setOpen(true);
  };

  return (
    <th
      className={numeric ? 'text-end' : undefined}
      style={{ whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2, background: '#f4f6f9' }}
      aria-sort={sortState === 'asc' ? 'ascending' : sortState === 'desc' ? 'descending' : 'none'}
    >
      <span className="d-inline-flex align-items-center gap-1">
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none fw-semibold d-inline-flex align-items-center gap-1"
          style={{ color: 'inherit', fontSize: 'inherit' }}
          onClick={cycleSort}
          title={`Sort by ${col.label}`}
          data-testid={`sort-${col.key}`}
        >
          {col.label}
          <span className={sortState === 'none' ? 'text-muted' : 'text-primary'}>
            <SortGlyph state={sortState} />
          </span>
        </button>
        <button
          type="button"
          className={`btn btn-sm p-0 border-0 bg-transparent position-relative ${selected.length ? 'text-primary' : 'text-muted'}`}
          style={{ lineHeight: 1 }}
          onClick={openMenu}
          title={selected.length ? `Filtered: ${selected.join(', ')}` : `Filter ${col.label}`}
          aria-label={`Filter ${col.label}`}
          aria-expanded={open}
          data-testid={`filter-${col.key}`}
        >
          <FilterGlyph />
          {selected.length > 0 && (
            <span
              className="badge rounded-pill bg-primary"
              style={{ fontSize: '0.55em', position: 'absolute', top: -6, right: -8 }}
            >
              {selected.length}
            </span>
          )}
        </button>
      </span>
      {open && anchor && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1080 }} />
          <div
            className="card shadow border-0 p-2 text-start fw-normal"
            role="dialog"
            aria-label={`Filter ${col.label}`}
            style={{ position: 'fixed', top: anchor.top, left: anchor.left, zIndex: 1090, width: 260 }}
          >
            <input
              className="form-control form-control-sm mb-2"
              autoFocus
              placeholder={`Type to find a ${col.label.toLowerCase()}…`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHi((h) => Math.min(h + 1, matches.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHi((h) => Math.max(h - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if (matches[hi] !== undefined) toggle(matches[hi]!);
                } else if (e.key === 'Escape') {
                  // Don't let the full-screen report's Esc handler close the whole modal.
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setOpen(false);
                }
              }}
              data-testid={`filter-input-${col.key}`}
            />
            <div style={{ maxHeight: 220, overflowY: 'auto' }} role="listbox" aria-multiselectable>
              {matches.length === 0 ? (
                <div className="text-muted small px-1 py-2">No matching values</div>
              ) : (
                matches.map((v, i) => {
                  const on = selected.includes(v);
                  return (
                    <div
                      key={v}
                      role="option"
                      aria-selected={on}
                      onMouseEnter={() => setHi(i)}
                      onClick={() => toggle(v)}
                      className="d-flex align-items-center gap-2 px-1 py-1 rounded small"
                      style={{ cursor: 'pointer', background: i === hi ? 'rgba(1,157,220,0.10)' : undefined }}
                      data-testid={`filter-option-${col.key}`}
                    >
                      <input type="checkbox" className="form-check-input m-0" readOnly checked={on} tabIndex={-1} />
                      <span className="text-truncate" title={v}>{v}</span>
                    </div>
                  );
                })
              )}
            </div>
            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
              <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => setSel([])} disabled={!selected.length}>
                Clear
              </button>
              <span className="text-muted" style={{ fontSize: 11 }}>
                {selected.length ? `${selected.length} selected` : `${values.length} values`}
              </span>
              <button type="button" className="btn btn-primary btn-sm py-0" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </th>
  );
}

// ── Grid ────────────────────────────────────────────────────────────────

/** "Pool A (EPV_SPV_IOT_…)" → name on one line, the device id small and muted beneath. */
function CellLabel({ text }: { text: string }) {
  const m = /^(.*\S)\s+\(((?:EPV|[A-Z0-9]{2,}_)[^)]*)\)$/.exec(text);
  if (!m) return <>{text}</>;
  return (
    <>
      <span style={{ whiteSpace: 'nowrap' }}>{m[1]}</span>
      <span className="d-block text-muted" style={{ fontSize: '0.78em' }}>
        {m[2]}
      </span>
    </>
  );
}

export interface DataGridProps {
  columns: ReportColumn[];
  /** All rows (unfiltered) — used for the filter value lists. */
  rows: Row[];
  /** Rows to show (already filtered + sorted by applyView). */
  visible: Row[];
  view: GridView;
  onView: (v: GridView) => void;
  maxHeight?: number;
  maxRows?: number;
}

export default function DataGrid({ columns, rows, visible, view, onView, maxHeight = 320, maxRows }: DataGridProps) {
  const [showAll, setShowAll] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const limit = maxRows && !showAll ? maxRows : visible.length;
  const shown = visible.slice(0, limit);
  const nFilters = activeFilterCount(view);

  const valuesByCol = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const c of columns) {
      const set = new Set<string>();
      for (const r of rows) set.add(formatCell(c, r[c.key]));
      const vals = [...set];
      if (c.type === 'number') vals.sort((a, b) => Number(a.replace(/,/g, '')) - Number(b.replace(/,/g, '')));
      else if (c.type === 'datetime' || c.type === 'date') {
        // keep chronological order of first appearance by sorting on the raw value
        const raw = new Map<string, number>();
        for (const r of rows) raw.set(formatCell(c, r[c.key]), sortValue(c, r[c.key]) as number);
        vals.sort((a, b) => (raw.get(b) ?? 0) - (raw.get(a) ?? 0));
      } else vals.sort((a, b) => a.localeCompare(b));
      out[c.key] = vals;
    }
    return out;
  }, [columns, rows]);

  return (
    <div>
      {nFilters > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-1 mb-1 small" data-testid="active-filters">
          <span className="text-muted">Showing {visible.length} of {rows.length}</span>
          {columns.flatMap((c) =>
            (view.filters[c.key] ?? []).map((v) => (
              <span key={`${c.key}:${v}`} className="badge rounded-pill text-bg-light border fw-normal">
                {c.label}: {v}
                <button
                  type="button"
                  className="btn-close ms-1"
                  style={{ fontSize: 7 }}
                  aria-label={`Remove filter ${c.label} ${v}`}
                  onClick={() =>
                    onView({
                      ...view,
                      filters: { ...view.filters, [c.key]: (view.filters[c.key] ?? []).filter((x) => x !== v) },
                    })
                  }
                />
              </span>
            )),
          )}
          <button type="button" className="btn btn-link btn-sm p-0 ms-1" onClick={() => onView({ ...view, filters: {} })}>
            Clear all
          </button>
        </div>
      )}
      <div ref={scrollRef} className="table-responsive" style={{ maxHeight, overflowY: 'auto' }}>
        <table className="table table-sm table-hover mb-0 small align-middle" data-testid="report-grid">
          <thead>
            <tr>
              {columns.map((c) => (
                <HeadCell key={c.key} col={c} values={valuesByCol[c.key] ?? []} view={view} onView={onView} />
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted py-3">
                  No rows match the filters.
                </td>
              </tr>
            ) : (
              shown.map((row, i) => (
                <tr key={i}>
                  {columns.map((c) => {
                    const href = linkFor(c, row);
                    const text = formatCell(c, row[c.key]);
                    return (
                      <td
                        key={c.key}
                        className={c.type === 'number' ? 'text-end' : undefined}
                        style={
                          c.key === 'description'
                            ? { maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
                            : c.type === 'number'
                              ? { fontVariantNumeric: 'tabular-nums' }
                              : undefined
                        }
                        title={c.key === 'description' ? String(row[c.key] ?? '') : undefined}
                      >
                        {href ? (
                          <Link to={href} className="text-decoration-none" title={c.link?.kind === 'pump' ? 'Open pump' : c.link?.kind === 'issue' ? 'Open issue' : 'Open user'} data-testid={`link-${c.link?.kind}`}>
                            <CellLabel text={text} />
                          </Link>
                        ) : (
                          <CellLabel text={text} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {visible.length > limit && (
        <button type="button" className="btn btn-link btn-sm px-0" onClick={() => setShowAll(true)}>
          Show all {visible.length} rows
        </button>
      )}
    </div>
  );
}
